import type { IAppSummary } from './application'
import type { DeviceType } from './device-type'
import type { IAssetFile } from './file'
import type { IAssetOption, IIapOption, ITranslationOption } from './option'
import type { IUserPublic } from './user'
import type { ReactElement, ReactNode } from 'react'

export type Orientation = 'both' | 'landscape' | 'portrait'

export interface IPlatformDependentProperties {
  builder_version?: string
  contextual_states: {
    identifier: string
    values: string[]
  }[]
  status_bar_style: string
  workspace_file_id?: string
}

export interface ITemplate {
  capabilities: string[]
  created_at: string
  created_by: string
  device_type: DeviceType
  id: string
  is_unity: boolean
  navigation_target_outputs: string[] | null
  orientation: Orientation
  placeholders: IPlaceholder[] | null
  platform_dependant_properties: IPlatformDependentProperties | null
  requirements: string[]
  status: string
  swipeable: boolean
  template_key: string
  title: string
  type: string
  updated_at: string
  updated_by: string
  version: string
}

export interface ITemplateRowData
  extends Omit<ITemplate, 'is_unity' | 'device_type' | 'updated_by' | 'template_key' | 'updated_at'> {
  deviceType: DeviceType
  isUnity: boolean
  templateKey: string
  updated_by?: IUserPublic | { name: string }
}

export type IPlaceholderType = 'translation' | 'iap' | 'asset' | 'color' | 'action' | 'embeddedUi'

export interface IPlaceholderRule {
  created_at: string
  created_by: string
  placeholder_key: string
  placeholder_type: IPlaceholderType
  rule_key: string
  template_version_id: string
  type: PLACEHOLDER_RULE_TYPES
  updated_at: string
  updated_by: string
  value: number | string | string[]
}

export interface IPlaceholder {
  created_at: string
  created_by: string
  key: string
  rules: IPlaceholderRule[] | null
  template_version_id: string
  type: IPlaceholderType
  updated_at: string
  updated_by: string
}

export interface IEmbeddedUiFormValue {
  iaps: IIapOption[] | undefined
  id: string | undefined
  navigationTargets: string[] | undefined
}

export interface IPlaceholderFormValue {
  category: string
  className?: string
  fieldName: string
  hiddenFields?: IPlaceholderType[]
  iapKey?: string
  key: string
  rules?: IPlaceholderRule[]
  type: IPlaceholderType
  value: string | ITranslationOption | IAssetOption | IOption | IEmbeddedUiFormValue
}

export interface IPageContainerFormValues {
  focus?: string
  isDisabled?: boolean
  isUnity: boolean
  placeholders: IPlaceholderFormValue[]
  screenshotFileIds: string[]
  selectedApp?: IAppSummary
  templateFilter?: string
  templateKey?: string
  templateVersion: string
  title: string
}

export interface IBlockPreviewProperties {
  text?: string
}

export interface IBlockProps {
  action?: string
  asset?: IAssetFile
  children?: ReactNode
  className?: string
  color?: string
  iap?: string
  onClick?: VoidFunction
  properties?: IBlockPreviewProperties
  translation?: string
}

export type IPlaceholderDefaultValue = Record<string, string | ITranslationOption>

export interface IPlaceholderConfig {
  category: string
  className?: string
  component: (props: IBlockProps) => ReactElement
  defaultValue?: IPlaceholderDefaultValue
  deviceType?: DeviceType
  filters?: string[]
  hiddenFields?: IPlaceholderType[]
  iapKey?: string
  placeholderKey: string
  previewProperties?: IBlockPreviewProperties
  type: IPlaceholderType | IPlaceholderType[]
}

export interface ITemplatePageConfig {
  backgroundImage?: string
  blocks?: ReactElement
  className?: string
  filters?: string[]
  hideRenderer?: boolean
  name?: string
  placeholders: IPlaceholderConfig[]
}

export interface ITemplateConfig {
  component: (props: ITemplateProps) => ReactElement
  config: ITemplatePageConfig[]
  keys: string[]
  orientation: 'portrait' | 'landscape'
}

export interface ITemplateConfigOptions extends Omit<ITemplateConfig, 'keys'> {
  key: string
}

export interface ITemplateProps {
  onBlockClick: (placeholderKey: string) => void
  pageIndex: number
  templateConfig: ITemplatePageConfig[]
}

export enum TEMPLATE_STATUS {
  ARCHIVED = 'archived',
  DRAFT = 'draft',
  READY = 'ready',
}

export enum TEMPLATE_TYPE {
  HORIZONTAL_MULTI_PAGE_CONTAINER = 'horizontal_multi_page_container',
  SINGLE_PAGE_CONTAINER = 'single_page_container',
}

export interface ITemplateParams {
  deviceType: string
  isUnity: boolean
  key: string
  version: string
}

export enum PLACEHOLDER_RULE_TYPES {
  MAX_LENGTH = 'max-len',
  MIN_LENGTH = 'min-len',
  REQUIRED_QUALITY_IDENTIFIER = 'required_quality_identifier',
}
